import { Injectable, OnInit } from '@angular/core';
import { Cookie } from 'ng2-cookies';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { AuthenticationService } from '../../auth';
import { Globals } from '../../globals';
import { Observable } from 'rxjs';

@Injectable()
export class PersonFormService implements OnInit {

    constructor(private http: HttpClient, private authService: AuthenticationService, private globals: Globals) { }

    ngOnInit() : void {
        this.authService.isTokenValid();
    }

    /**
     * Get person by id
     * @param personId identification of person
     */
    getPersonById(personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/' + personId;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }
    
    

    /**
     * Get person by ssn
     * @param personSsn identification of person
     */
    getPersonBySsn(personSsn) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/ssn/' + personSsn;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }
    
    /**
     * Get person by ssn or coordination number
     * @param identityNumber identification of person
     */
    getPersonByIdentityNumber(identityNumber) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/identityNumber/' + identityNumber;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Get person by coordination number
     * @param personSsn identification of person
     */
    getPersonByCoordinationNumber(personCoordinationNumber) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/cordinationNumber/' + personCoordinationNumber;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Validate if person by ssn already exists
     * @param personSsn identification of person
     */
    doesPersonBySsnExists(personSsn) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/ssn/validate/' + personSsn;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Validate if person by ssn already exists
     * @param coordinationNumber person coordination number
     */
    doesPersonByCoordinationNumberExists(coordinationNumber) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/coordinationNumber/validate/' + coordinationNumber;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Save new person
     * @param person person object
     */
    savePerson(person, personGroupList) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person';
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        const body = new HttpParams()
        .set('personBody', JSON.stringify(person))
        .set('groupListBody', JSON.stringify(personGroupList));

        return this.http.post(url, body, {headers: requestHeaders, responseType: 'json'});
    }

    /**
     * Update existing person
     * @param person person object
     * @param person personGroupList object
     * @param person rkOrgAssociation object
     */
    updatePerson(person, personGroupList, rkOrgAssociation) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/' + person.id ;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        const body = new HttpParams()
        .set('personBody', JSON.stringify(person))
        .set('groupListBody', JSON.stringify(personGroupList))
        .set('rkOrgAssociationBody', JSON.stringify(rkOrgAssociation));

        return this.http.put(url, body, {headers: requestHeaders, responseType: 'json'});
    }

    /**
     * Get all employers
     */
    getAllEmployers() : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/employer/';

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());


        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Get all employers based on personId
     * This is done in order to set a selected department to the entity
     */
    getAllEmployersByPersonId(personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/employer/person/' + personId;

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());


        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Get all projects
     */
    getAllFasProjects() {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/securityAgreement/';

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }
    
    getAllDepartments() {
        this.authService.isTokenValid();
      const url = this.authService.getResourceURL() + '/api/v1/govdepartment/';
      let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    getOwnerGroups() : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/group/owner/all';
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }
    
}
